import React from "react";
import {
  Modal,
  Paper,
  Typography,
  Stack,
  Box,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

/**
 * Modal reutilizable para mostrar contenido de ayuda contextual.
 * Renderiza título, descripción y secciones con listas de items.
 *
 * @param {{ open, onClose, content: { title, description, sections: [{ heading, items }] } }} props
 */
export default function InfoModal({ open, onClose, content }) {
  if (!content) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: { xs: "95%", sm: 540 },
          maxHeight: "85vh",
          overflow: "auto",
          p: 3,
          borderRadius: 3,
          outline: "none",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "#E2E8F0",
            borderRadius: 3,
          },
        }}
      >
        <Stack spacing={2.5}>
          {/* Header */}
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {content.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5, lineHeight: 1.5 }}
              >
                {content.description}
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose} sx={{ ml: 1, mt: -0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Divider />

          {/* Sections */}
          {content.sections?.map((section, idx) => (
            <Box key={idx}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{
                  mb: 1,
                  color: "#2563EB",
                  fontSize: "0.82rem",
                }}
              >
                {section.heading}
              </Typography>
              <Stack spacing={0.75}>
                {section.items?.map((item, i) => (
                  <Stack
                    key={i}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <FiberManualRecordIcon
                      sx={{
                        fontSize: 6,
                        mt: 0.85,
                        color: "#94A3B8",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.8rem",
                        lineHeight: 1.5,
                        color: "text.primary",
                      }}
                    >
                      {formatHelpItem(item)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </Paper>
    </Modal>
  );
}

/**
 * Formatea un item de ayuda: texto antes del primer " — " se hace bold.
 */
function formatHelpItem(text) {
  const dashIndex = text.indexOf(" — ");
  if (dashIndex === -1) return text;

  const label = text.substring(0, dashIndex);
  const desc = text.substring(dashIndex + 3);

  return (
    <>
      <strong>{label}</strong> — {desc}
    </>
  );
}
